import React from 'react';
import { Alert, AlertTitle, Box, Typography, alpha } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';

/**
 * Strong authorized-access / legal disclaimer shown on public pages
 * (Sign In, Register, Landing, Browsing).
 */
export default function AuthorizedAccessDisclaimer({
  companyName,
  variant = 'panel',
  textPrimary,
  textSecondary,
  sx = {}
}) {
  const company = (companyName && String(companyName).trim()) || 'this organization';

  const title = 'AUTHORIZED ACCESS ONLY — LEGAL NOTICE';
  const body = (
    <>
      This Software-as-a-Service (SaaS) platform is provided exclusively for people who are authorized
      by <strong>{company}</strong> to sign in. Any unauthorized, anonymous, or fraudulent login attempt
      is strictly prohibited, will be blocked immediately, and may result in civil and/or criminal legal
      action. Access is monitored and logged.
      {' '}
      Customer logo and customer information for <strong>{company}</strong> are displayed on this service,
      including the public landing page, as configured for this tenant.
    </>
  );

  if (variant === 'alert') {
    return (
      <Alert
        severity="error"
        variant="outlined"
        icon={<GavelIcon fontSize="inherit" />}
        role="note"
        aria-label={title}
        sx={{
          mb: 2.5,
          alignItems: 'flex-start',
          borderWidth: 2,
          borderColor: 'error.main',
          bgcolor: (t) => alpha(t.palette.error.main, 0.08),
          '& .MuiAlert-message': { width: '100%' },
          ...sx
        }}
      >
        <AlertTitle sx={{ fontWeight: 800, letterSpacing: 0.02, mb: 0.75 }}>{title}</AlertTitle>
        <Typography variant="body2" sx={{ lineHeight: 1.5, fontWeight: 500 }}>
          {body}
        </Typography>
      </Alert>
    );
  }

  return (
    <Box
      component="aside"
      role="note"
      aria-label={title}
      sx={{
        mx: 'auto',
        maxWidth: 920,
        px: { xs: 2, sm: 3 },
        py: 1.75,
        borderRadius: 1.5,
        border: '2px solid',
        borderColor: 'error.main',
        bgcolor: (t) => alpha(t.palette.error.main, 0.07),
        textAlign: 'left',
        ...sx
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 800,
          letterSpacing: 0.04,
          color: textPrimary || 'error.dark',
          mb: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75
        }}
      >
        <GavelIcon sx={{ fontSize: 18 }} />
        {title}
      </Typography>
      <Typography
        variant="caption"
        component="p"
        sx={{
          m: 0,
          display: 'block',
          lineHeight: 1.55,
          fontWeight: 500,
          color: textSecondary || 'text.secondary'
        }}
      >
        {body}
      </Typography>
    </Box>
  );
}
